// ─────────────────────────────────────────────────────────────────────────────
// IDENTITY — the existing app-index identity path, reused (Ticket 6 #2).
//
// This is deliberately NOT a new auth system. The Authority One account layer
// is Supabase Auth; the JWT it issues is the bearer that the runtime's
// app-channel already accepts on every `/app/*` route:
//
//   console.resolvePrincipal → Supabase-JWT branch → verifySupabaseJwt
//                            → the app-index user→handle mapping
//                            → principal.account = the fan's OWN agent handle
//
// So a signed-in fan's `/app/rank-candidates` call is routed, server-side and
// unforgeably, to *their* Durable Object. The show-space never names an agent,
// never learns a handle, and cannot target anyone else's DO — omitting the
// optional `agent` field is exactly the owner-self path the app fork uses.
//
// This file mirrors the app fork's `src/lib/supabase/client.ts`, minus React
// Native: the web build uses the default localStorage session store and the
// same PKCE flow with `detectSessionInUrl` so magic-link redirects land.
// ─────────────────────────────────────────────────────────────────────────────

import {createClient} from '@supabase/supabase-js'

import {SUPABASE_ANON_KEY, SUPABASE_URL} from './config'

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Persist + rotate the access token before expiry, so a long-lived show
    // page keeps a valid bearer for /app/rank-candidates.
    autoRefreshToken: true,
    persistSession: true,
    // Parse the `#access_token=…` fragment a magic-link redirect lands on.
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
})

/**
 * Resolve a currently-valid access token, refreshing if it is missing or within
 * ~60s of expiry. Returns `null` when signed out.
 *
 * Same contract as the app fork's `getFreshAccessToken()`, so every runtime
 * request carries a fresh, unexpired bearer.
 */
export async function getFreshAccessToken(): Promise<string | null> {
  const {data, error} = await supabase.auth.getSession()
  if (error || !data.session) return null

  const session = data.session
  const expiresAtMs = (session.expires_at ?? 0) * 1000
  const aboutToExpire = expiresAtMs > 0 && expiresAtMs - Date.now() < 60_000

  if (aboutToExpire) {
    const refreshed = await supabase.auth.refreshSession()
    if (!refreshed.error && refreshed.data.session) {
      return refreshed.data.session.access_token
    }
    // Refresh failed (e.g. offline) — fall back to the still-cached token.
  }
  return session.access_token ?? null
}

/** Send a magic link. The redirect returns to this show-space origin. */
export async function signInWithEmail(email: string): Promise<{error: string | null}> {
  const {error} = await supabase.auth.signInWithOtp({
    email,
    options: {emailRedirectTo: window.location.origin + window.location.pathname},
  })
  return {error: error?.message ?? null}
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut()
}
